/**
 * Stripe contract-details predicates + status messaging, ported 1:1 from the mobile
 * screen (`gigmi-mobile/App/containers/NewContractDetailsScreen/helperFunctions.js`) so
 * web and mobile agree on who pays, who is a payee-sender, and what the WAITING/BLOCKED
 * states mean. Shared across ContractDetailsMainButton, ContractButtons, and the
 * contract-details container. Every predicate is Stripe-only where money direction
 * differs from the legacy provider; callers gate the rest with `isStripe()`.
 *
 * `userType` here is the caller's `user_type` string ("homeowner"|"contractor"|
 * "tradesman"), matching the web `userData.user_type` / STRINGS constants.
 */
import STRINGS from "../../utils/UserTypes";
import { isStripe } from "../../utils/paymentProvider";

/**
 * A payee-SENDER created a quick contract addressed to the party who will pay: a
 * gigster who sent `direct_pay_to_contractor`, or a contractor who sent
 * `direct_pay_to_homeowner`. They receive the money, so they never pay here — the
 * payer pays on accept. (A tradesman RECEIVING `direct_pay_to_tradesman` is the
 * accepter/payer and must NOT match.)
 */
export const isQuickContractPayeeSender = (contractDetail, userType) => {
  const isGigsterToContractor =
    userType === STRINGS.TRADESMAN_TYPE &&
    contractDetail?.status === "direct_pay_to_contractor";
  const isContractorToHomeowner =
    userType === STRINGS.CONTRACTOR_TYPE &&
    contractDetail?.status === "direct_pay_to_homeowner";
  return isGigsterToContractor || isContractorToHomeowner;
};

export const isQuickContractPayeeAwaitingPayment = (contractDetail, userType) =>
  contractDetail?.primary_status === "waiting" &&
  isQuickContractPayeeSender(contractDetail, userType);

/**
 * Is the current user the PAYER (the one charged)? Backend rule: payer = homeowner
 * when homeownerId is set, else the contractor. A tradesman is never the payer; a
 * contractor is the payer only on a contract with no homeowner (a subcontract leg).
 */
export const isContractPayer = (contractDetail, userType) => {
  if (contractDetail?.homeownerId) {
    return userType === STRINGS.HOMEOWNER_TYPE;
  }
  return userType === STRINGS.CONTRACTOR_TYPE;
};

/**
 * Is the current user a quick-contract RECEIVER (the payee-accepter)? Used to gate the
 * "set up payouts first" redirect on Accept — mirrors mobile `isQuickContractReceiver`.
 * `contractedType` is the counterparty's user type.
 */
export const isQuickContractReceiver = (userType, contractedType) =>
  (contractedType === STRINGS.HOMEOWNER_TYPE &&
    (userType === STRINGS.CONTRACTOR_TYPE ||
      userType === STRINGS.TRADESMAN_TYPE)) ||
  (contractedType === STRINGS.CONTRACTOR_TYPE &&
    userType === STRINGS.TRADESMAN_TYPE);

/**
 * Flow C — a Stripe subcontract (`parentContractId` set) parked at WAITING with no
 * money secured yet (`is_funded === false`): the award was created but the contractor
 * hasn't finished (or cancelled) the PaymentSheet. The contractor sees a Pay-Now
 * banner; the gigster's Accept is blocked until it clears.
 */
export const isSubcontractAwaitingHold = (contractDetail) =>
  isStripe() &&
  Boolean(contractDetail?.parentContractId) &&
  contractDetail?.primary_status === "waiting" &&
  contractDetail?.is_funded === false;

/**
 * A top-level standard contractor→gigster contract awaiting the contractor's auth-hold:
 * no parent (`parentContractId` unset), no homeowner leg, not a quick contract
 * (`direct_pay_*`), parked at WAITING with no money secured (`is_funded === false`), and
 * viewed by the contractor (the payer). Mirrors `isSubcontractAwaitingHold` for the
 * non-subcontract case so the contractor sees the same Pay-Now banner; `isContractPayer`
 * keeps the banner off the gigster's view.
 */
export const isStandardContractAwaitingHold = (contractDetail, userType) =>
  isStripe() &&
  !contractDetail?.parentContractId &&
  !contractDetail?.homeownerId &&
  !contractDetail?.status?.startsWith("direct_pay_") &&
  contractDetail?.primary_status === "waiting" &&
  contractDetail?.is_funded === false &&
  isContractPayer(contractDetail, userType);

/**
 * A gigster must not accept a standard contract the payer hasn't funded. The
 * sequence is auth-hold-then-accept: the payer funds (Stripe charge/hold) before
 * the gigster accepts, and the accept captures the hold into escrow. `is_funded`
 * (a settled charge) is the only reliable funded signal — `primary_status` only
 * flips to WAITING when the payment sheet is created, so a contract the payer
 * never opened the sheet on has no waiting marker and would otherwise show an
 * enabled Accept. TRADESMAN-only: a payer accepting (contractor/homeowner) is
 * what triggers the charge, so `is_funded === false` is expected on their path and
 * must NOT be gated. Client defense-in-depth — the backend accept endpoint is the
 * authoritative boundary.
 */
export const isGigsterAcceptOnUnfundedContract = (contractDetail, userType) =>
  isStripe() &&
  userType === STRINGS.TRADESMAN_TYPE &&
  ["assigned", "send"].includes(contractDetail?.status) &&
  contractDetail?.is_funded === false;

/**
 * WAITING info copy under Stripe. WAITING means different things to payer vs payee,
 * and `is_funded` (money secured by hold or charge, NOT a completed payout) splits it
 * further into four messages.
 */
export const getStripeWaitingBody = (contractDetail, userType) => {
  const paid = Boolean(contractDetail?.is_funded);
  if (isContractPayer(contractDetail, userType)) {
    return paid
      ? "STRIPE_PAYER_PAYMENT_CONFIRMED"
      : "STRIPE_PAYER_PAYMENT_PENDING";
  }
  return paid ? "STRIPE_PAYEE_PAYOUT_PENDING" : "STRIPE_PAYEE_AWAITING_PAYER";
};

/**
 * The message-id for the waiting/blocked status-info banner. Returns an i18n id (not
 * text) so the caller renders it via <FormattedMessage>. Mirrors mobile
 * `getStatusInfoBody`.
 */
export const getStatusInfoBody = (contractDetail, userType) => {
  if (isQuickContractPayeeAwaitingPayment(contractDetail, userType)) {
    return "QC_PENDING_PAYMENT";
  }
  if (isStripe()) {
    return contractDetail?.primary_status === "waiting"
      ? getStripeWaitingBody(contractDetail, userType)
      : "STRIPE_PAYMENT_BLOCKED";
  }
  return contractDetail?.primary_status === "waiting"
    ? "TRANSACTION_BEING_PROCESSED"
    : "TRANSACTION_IS_BLOCKED";
};

/**
 * Is the contract-amount change-request (edit) flow available? Under Stripe it is NOT:
 * the delta money handling (delta-up charge / delta-down wallet credit) is deferred to
 * post-launch, and the backend revision path has known defects — an upward revision
 * never charges the difference, the payer is then blocked from topping up, and release
 * fails against the under-funded charge. Hiding the entry point is the same position
 * mobile takes in `right_icon_generator`. Legacy/aeropay keeps the flow.
 */
export const isChangeRequestFlowAvailable = () => !isStripe();
